// schema
import { IViewer } from "@schema";

// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";
import { Router } from "found";
import withStyles, { WithStyles } from "@material-ui/core/styles/withStyles";
import { Avatar, Divider, ListItemText, Menu, MenuItem } from "@material-ui/core";
import {
  AccountCircle,
  AddCircleOutline,
  ArrowDropDown,
  Feedback,
  Help,
  ExitToApp,
  Send,
  Settings,
} from "@material-ui/icons";

// components
import { H2 } from "@ui";
import { LogoutBtn } from "@components/logout-btn";

// constants
import { baseUrl } from "@clockwise/client-commons/src/constants/help-center";
import { paths } from "@constants/site.constant";

// state
import { IReduxState } from "@reducers/root.reducer";
import { setSettingsDialogOpen } from "@actions/chrome-extension.actions";
import { toggleExpanded } from "@actions/settings.actions";
import { ISettingsState } from "@reducers/settings.reducer";
import { getOnboardingFlowState } from "@state/onboarding";

// util
import { track, TrackingEvents } from "@util/analytics.util";
import { getCurrentOrg } from "@util/org.util";
import { filterAndSortCategories } from "@util/event-category-coloring.util";
import { sortDataSources } from "@util/data-source.util";

// internal
import { fragments } from "./WebAppNavAvatarMenu.gql";
import { styles } from "./WebAppNavAvatarMenu.styles";

interface IContainer {
  router?: Router;
  hideAdvancedMenu?: boolean;
  viewer: IViewer;
}

interface IProps extends IContainer, WithStyles<typeof styles> {
  setSettingsDialogOpen: typeof setSettingsDialogOpen;
  toggleExpanded: typeof toggleExpanded;
  fastCrawlFinished: boolean;
}

interface IState {
  anchorEl: (EventTarget & HTMLDivElement) | null;
  advancedAnchorEl: (EventTarget & HTMLDivElement) | null;
}

export class WebAppNavAvatarMenuComponent extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);

    this.state = {
      anchorEl: null,
      advancedAnchorEl: null,
    };
  }

  //////////////
  // Handlers //
  //////////////

  private openSettingsDialog = (settingsStateKey?: keyof ISettingsState) => {
    this.onCloseMenu();
    this.onCloseAdvancedMenu();
    this.props.setSettingsDialogOpen(true);
    if (settingsStateKey) {
      this.props.toggleExpanded(settingsStateKey);
    }
  };

  private onOpenMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    track(TrackingEvents.APP_NAV.OPEN_MENU);

    this.setState({ anchorEl: event.currentTarget });
  };

  private onOpenAdvancedMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    track(TrackingEvents.APP_NAV.OPEN_ADVANCED_MENU);

    this.setState({ advancedAnchorEl: event.currentTarget });
  };

  private onCloseMenu = () => {
    this.setState({ anchorEl: null });
  };

  private onCloseAdvancedMenu = () => {
    this.setState({ advancedAnchorEl: null });
  };

  private onClickSettings = () => {
    track(TrackingEvents.APP_NAV.MENU_SETTINGS);
    this.onCloseMenu();
    this.props.setSettingsDialogOpen(true);
  };

  private onClickLogout = () => {
    track(TrackingEvents.APP_NAV.MENU_LOGOUT);
    this.onCloseMenu();
    if (this.props.router) {
      this.props.router.replace(paths.login);
    }
  };

  private maybeRenderOptionalAdvancedMenuItems() {
    const { viewer } = this.props;
    const org = getCurrentOrg(viewer);

    if (!org) {
      return null;
    }

    // personal calendar
    const sortedDataSources = sortDataSources(viewer.user.dataSources.edges, org.domains);
    const showPersonalCalendar = sortedDataSources.other.length === 0;
    // slack
    const slackAccounts =
      org.slackAccountsErrorable.__typename !== "GraphEntityError" &&
      org.slackAccountsErrorable.list;
    const showSlack = slackAccounts && slackAccounts.length === 0;
    // color coding
    const eventCategoryColorings = filterAndSortCategories(org.eventColoringSettingsErrorable);
    const showColorCoding = eventCategoryColorings.every((ecc) => !ecc.active);

    if (!showPersonalCalendar && !showSlack && !showColorCoding) {
      return null;
    }

    return [
      <Divider key="advanced-divider" style={{ marginTop: 12, marginBottom: 12 }} />,
      <H2 key="advaned-header" className={this.props.classes.menuHeader}>
        More features
      </H2>,
      showPersonalCalendar ? (
        <MenuItem
          key="personal-calendar"
          onClick={() => {
            track(TrackingEvents.APP_NAV.MENU_PERSONAL_CALENDAR);
            this.openSettingsDialog("expandPersonalCalendar");
          }}
        >
          <AddCircleOutline className={this.props.classes.menuItemIcon} />
          <ListItemText inset primary="Personal calendar" />
        </MenuItem>
      ) : null,
      showSlack ? (
        <MenuItem
          key="slack"
          onClick={() => {
            track(TrackingEvents.APP_NAV.MENU_SLACK);
            this.openSettingsDialog("expandIntegrations");
          }}
        >
          <AddCircleOutline className={this.props.classes.menuItemIcon} />
          <ListItemText inset primary="Slack integration" />
        </MenuItem>
      ) : null,
      showColorCoding ? (
        <MenuItem
          key="color-coding"
          onClick={() => {
            track(TrackingEvents.APP_NAV.MENU_COLOR_CODING);
            this.openSettingsDialog("expandColorCoding");
          }}
        >
          <AddCircleOutline className={this.props.classes.menuItemIcon} />
          <ListItemText inset primary="Event coloring" />
        </MenuItem>
      ) : null,
    ];
  }

  public render() {
    const { user } = this.props.viewer;

    if (!user) {
      return null;
    }

    const onboardingState = getOnboardingFlowState(user);

    const org = getCurrentOrg(this.props.viewer);

    const showAdvancedActions =
      onboardingState &&
      onboardingState.current.state === "Finished" &&
      this.props.fastCrawlFinished;

    const userProfile =
      org &&
      org.userPerson &&
      org.userPerson.__typename !== "GraphEntityError" &&
      org.userPerson &&
      org.userPerson.profile;

    const externalImageUrl = userProfile ? userProfile.externalImageUrl : user.externalImageUrl;

    const avatarAlt = userProfile
      ? `${userProfile.givenName} ${userProfile.familyName}`
      : user.givenName
      ? `${user.givenName} ${user.familyName}`
      : (org && org.primaryOrgCalendar) || "";

    return (
      <div className={this.props.classes.menus}>
        {showAdvancedActions && !this.props.hideAdvancedMenu ? (
          <React.Fragment>
            <div className={this.props.classes.advancedMenu} onClick={this.onOpenAdvancedMenu}>
              More
              <ArrowDropDown className={this.props.classes.menuExpandIcon} />
            </div>
            <Menu
              className={this.props.classes.menu}
              open={!!this.state.advancedAnchorEl}
              anchorEl={this.state.advancedAnchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              onClose={this.onCloseAdvancedMenu}
              onBackdropClick={this.onCloseAdvancedMenu}
              onEscapeKeyDown={this.onCloseAdvancedMenu}
            >
              <MenuItem
                onClick={() => {
                  track(TrackingEvents.APP_NAV.MENU_INVITE);
                  this.openSettingsDialog("expandInvites");
                }}
              >
                <Send className={this.props.classes.menuItemIcon} />
                <ListItemText inset primary="Invite coworkers" />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  track(TrackingEvents.APP_NAV.MENU_FEEDBACK);
                  window.open("https://support.getclockwise.com/hc/en-us/requests/new", "_blank");
                  this.onCloseAdvancedMenu();
                }}
              >
                <Feedback className={this.props.classes.menuItemIcon} />
                <ListItemText inset primary="Contact support" />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  track(TrackingEvents.APP_NAV.MENU_HELP);
                  window.open(baseUrl, "_blank");
                  this.onCloseAdvancedMenu();
                }}
              >
                <Help className={this.props.classes.menuItemIcon} />
                <ListItemText inset primary="Help center" />
              </MenuItem>
              {this.maybeRenderOptionalAdvancedMenuItems()}
            </Menu>
          </React.Fragment>
        ) : null}
        <Avatar
          className={this.props.classes.avatar}
          onClick={this.onOpenMenu}
          alt={avatarAlt}
          src={externalImageUrl}
        >
          {avatarAlt[0]}
        </Avatar>
        <Menu
          className={this.props.classes.menu}
          open={!!this.state.anchorEl}
          anchorEl={this.state.anchorEl}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          onClose={this.onCloseMenu}
          onBackdropClick={this.onCloseMenu}
          onEscapeKeyDown={this.onCloseMenu}
        >
          {showAdvancedActions
            ? [
                <MenuItem key="settings" onClick={this.onClickSettings}>
                  <Settings className={this.props.classes.menuItemIcon} />
                  <ListItemText inset primary="Settings" />
                </MenuItem>,
                <Divider key="settings-divider" className={this.props.classes.menuDivider} />,
              ]
            : null}
          {showAdvancedActions
            ? [
                <MenuItem
                  key="account"
                  onClick={() => {
                    track(TrackingEvents.APP_NAV.MENU_ACCOUNT);
                    this.openSettingsDialog("expandAccount");
                  }}
                >
                  <AccountCircle className={this.props.classes.menuItemIcon} />
                  <ListItemText inset primary="Account details" />
                </MenuItem>,
                <Divider key="account-divider" className={this.props.classes.menuDivider} />,
              ]
            : null}
          <LogoutBtn
            onLogout={this.onClickLogout}
            isNewMenuItem
            menuItemIcon={<ExitToApp className={this.props.classes.menuItemIcon} />}
          />
        </Menu>
      </div>
    );
  }
}

/////////////////
// With Styles //
/////////////////

const WebAppNavAvatarMenuWithStyles = withStyles(styles)(WebAppNavAvatarMenuComponent);

///////////
// Redux //
///////////

function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    fastCrawlFinished: state.onboarding.fastCrawlFinished,
  };
}

const WebAppNavAvatarMenuRedux = connect(mapStateToProps, {
  setSettingsDialogOpen,
  toggleExpanded,
})(WebAppNavAvatarMenuWithStyles);

///////////
// Relay //
///////////

export const WebAppNavAvatarMenu = createFragmentContainer<IContainer>(
  WebAppNavAvatarMenuRedux,
  fragments,
);
